import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PetFoodCallout from "../components/PetFoodCallout";
import { Button } from "../components/ui/button";
import { currency } from "../lib/format";
import {
    Heart,
    Repeat,
    Package,
    HandHeart,
    Home as HomeIcon,
    Truck,
    Building2,
    Bot,
    Sparkles,
    ArrowRight,
    Coins,
    Bell,
    Bone,
} from "lucide-react";

function GiveCard({ icon: Icon, title, body, cta, to, external, badge, tone, testid }) {
    const inner = (
        <div className={`card-warm rounded-2xl p-6 flex flex-col h-full transition-all group hover:-translate-y-0.5 hover:border-[var(--rl-primary)] ${tone === "primary" ? "!bg-[var(--rl-primary)] !border-[var(--rl-primary)] !text-white" : ""}`}>
            <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-4 ${tone === "primary" ? "bg-white/15 text-white" : "bg-[var(--rl-cream)] text-[var(--rl-text)]"}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
                <h3 className="font-serif text-2xl leading-tight">{title}</h3>
                {badge && (
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${tone === "primary" ? "bg-white/15 text-white" : "bg-[var(--rl-cream)] text-[var(--rl-text-2)]"}`}>{badge}</span>
                )}
            </div>
            <p className={`mt-2 text-sm leading-relaxed flex-1 ${tone === "primary" ? "text-white/85" : "text-[var(--rl-text-2)]"}`}>{body}</p>
            <span className={`mt-5 inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2.5 transition-all ${tone === "primary" ? "text-white" : "text-[var(--rl-primary)]"}`}>
                {cta} <ArrowRight className="w-4 h-4" />
            </span>
        </div>
    );
    return external
        ? <a href={to} data-testid={testid} className="block h-full">{inner}</a>
        : <Link to={to} data-testid={testid} className="block h-full">{inner}</Link>;
}

export default function WaysToGive() {
    const [donations, setDonations] = useState(null);
    const [resources, setResources] = useState(null);

    useEffect(() => {
        api.get("/donations/stats").then((r) => setDonations(r.data)).catch(() => {});
        api.get("/stats/resources").then((r) => setResources(r.data)).catch(() => {});
    }, []);

    const raised = donations?.total ?? resources?.total_raised_usd ?? 0;
    const donors = donations?.count ?? resources?.donations_count ?? 0;

    return (
        <div data-testid="ways-to-give-page">
            {/* Hero */}
            <section className="bg-gradient-to-br from-[#fff5f3] to-[var(--rl-cream)] border-b border-[var(--rl-border)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7">
                        <div className="eyebrow mb-4 inline-flex items-center gap-2 text-[var(--rl-primary)]">
                            <Heart className="w-3.5 h-3.5" fill="currentColor" /> Ways to Give
                        </div>
                        <h1 className="font-serif text-[42px] leading-[1.05] sm:text-6xl tracking-tight">
                            Rescues are running on empty.<br />
                            <em className="not-italic text-[var(--rl-primary)]">Pick the way you can help.</em>
                        </h1>
                        <p className="mt-6 text-lg text-[var(--rl-text-2)] max-w-xl leading-relaxed">
                            Money is powerful, but so are a spare crate, a Saturday drive, a foster room, or a company match. Every path below routes directly to a rescue in need — no middlemen, no admin drag.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/donate"><Button className="btn-primary rounded-full h-12 px-6" data-testid="give-donate-btn"><Heart className="w-4 h-4 mr-2" fill="currentColor" /> Donate now</Button></Link>
                            <Link to="/foster"><Button variant="outline" className="rounded-full h-12 px-6 border-[var(--rl-border-strong)]">Foster a dog</Button></Link>
                            <Link to="/volunteer"><Button variant="outline" className="rounded-full h-12 px-6 border-[var(--rl-border-strong)]">Volunteer</Button></Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5 grid grid-cols-2 gap-3">
                        <div className="card-warm rounded-xl p-4"><div className="font-serif text-4xl text-[var(--rl-primary)]">{currency(raised)}</div><div className="eyebrow mt-1">raised to date</div></div>
                        <div className="card-warm rounded-xl p-4"><div className="font-serif text-4xl text-[var(--rl-primary)]">{donors}</div><div className="eyebrow mt-1">donors</div></div>
                        <div className="card-warm rounded-xl p-4"><div className="font-serif text-4xl text-[var(--rl-urgent)]">{resources ? resources.at_risk : "—"}</div><div className="eyebrow mt-1">dogs at risk right now</div></div>
                        <div className="card-warm rounded-xl p-4"><div className="font-serif text-4xl text-[var(--rl-primary)]">100%</div><div className="eyebrow mt-1">direct to rescue</div></div>
                    </div>
                </div>
            </section>

            {/* Pet Food callout — donate variant */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8">
                <PetFoodCallout variant="donate" />
            </section>

            {/* The nine ways */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <div className="mb-10">
                    <div className="eyebrow text-[var(--rl-primary)] mb-3">Nine ways, one goal</div>
                    <h2 className="font-serif text-3xl sm:text-5xl tracking-tight max-w-3xl">Every kind of help a rescue actually needs.</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ways-grid">
                    <GiveCard
                        icon={Heart} tone="primary" badge="Most common"
                        title="One-time donation"
                        body="Direct dollars to the Global Fund or an individual dog. Live Stripe checkout, receipt in your inbox, 100% routes to a rescue partner."
                        cta="Give now" to="/donate" testid="give-onetime"
                    />
                    <GiveCard
                        icon={Repeat} badge="Highest impact"
                        title="Monthly recurring"
                        body="Predictable income lets rescues take chances on medical cases and cross-border pulls. Cancel anytime from your dashboard."
                        cta="Set up monthly" to="/donate?recurring=1" testid="give-monthly"
                    />
                    <GiveCard
                        icon={Package}
                        title="Donate pet food"
                        body="The largest recurring line item on any rescue's balance sheet. Drop-off, ship, coordinate a bulk donation, or set up a brand partnership."
                        cta="Donate food" to="/pet-food" testid="give-supplies"
                    />
                    <GiveCard
                        icon={HomeIcon}
                        title="Foster a dog"
                        body="A foster home saves two lives — the dog you take in and the one who takes their kennel. Short-term (2 weeks) through medical-recovery options."
                        cta="Become a foster" to="/foster" testid="give-foster"
                    />
                    <GiveCard
                        icon={HandHeart}
                        title="Volunteer your skills"
                        body="Dog walking, transport driving, remote data entry, translation, event help, photography, veterinary triage, social media, legal escort — every hour matters."
                        cta="Find an opportunity" to="/volunteer" testid="give-volunteer"
                    />
                    <GiveCard
                        icon={Truck}
                        title="Drive a transport leg"
                        body="Claim a 40-mile leg on the live route map and get a dog closer to their foster. Zero fundraising required — just gas and an afternoon."
                        cta="Open transport map" to="/transport" testid="give-drive"
                    />
                    <GiveCard
                        icon={Building2} tone="primary" badge="Tax-smart"
                        title="Stock, DAF, crypto"
                        body="Appreciated stock, Donor-Advised Fund grants, crypto, and wire transfers. Corporate-match programs get set up in one session with our Wingman."
                        cta="Corporate portal" to="/corporate" testid="give-corporate"
                    />
                    <GiveCard
                        icon={Bell}
                        title="Boost an urgent ask"
                        body="Subscribe to Live Rescue Alerts and re-share meat-trade or 48-hour dogs. Reach beats budget every single time on a countdown."
                        cta="Get alerts" to="/alerts" testid="give-alerts"
                    />
                    <GiveCard
                        icon={Bot} badge="Keeps AI free"
                        title="Robot Snacks server fund"
                        body="Micro-donations that pay for the platform's LLM + server costs so translation, matchmaking, and rescue analysis stay free for every partner."
                        cta="Feed the robot" to="/robot-snacks" testid="give-snacks"
                    />
                </div>
            </section>

            {/* Employer match strip */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
                <div className="rounded-2xl bg-[var(--rl-cream)] border border-[var(--rl-border)] p-8 lg:p-10 grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center" data-testid="employer-match-strip">
                    <div className="w-14 h-14 rounded-full bg-[var(--rl-primary)] text-white flex items-center justify-center">
                        <Coins className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="eyebrow text-[var(--rl-primary)] mb-1">Employer match</div>
                        <div className="font-serif text-2xl leading-tight">Your company might double every dollar you give.</div>
                        <p className="text-sm text-[var(--rl-text-2)] mt-2 max-w-2xl">Check with your HR portal (Benevity, Bright Funds, Millie, etc.) — RescueLink is 501(c)(3)-fiscally sponsored through partner rescues, which means matches almost always qualify.</p>
                    </div>
                    <Link to="/corporate"><Button variant="outline" className="rounded-full h-11 px-5 border-[var(--rl-primary)] text-[var(--rl-primary)]">Match my gift</Button></Link>
                </div>
            </section>
        </div>
    );
}
